import { OrderType } from 'constants/order';
import { useCart } from 'contexts/cart';
import { useTrackingContext } from 'contexts/tracking';
import { useProductDetailsHeaderState } from 'modules/product-details/components/product-details-header/state';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { Gtm, gtm } from 'tracking/gtm';

const useProductDetailsCart = (): {
  onAddToCart: () => Promise<null | undefined>;
} => {
  const {
    crossSellProductDetails,
    crossSellProductQuantity,
    orderType,
    price,
    product,
    selectedVariant,
    selectedVariantId,
    subscriptionInterval,
    variantQuantity,
    setCrossSellProductQuantity,
  } = useProductDetailsHeaderState();

  const { addProductsToCart, addSubItemToCart } = useCart();

  const { getProductList } = useTrackingContext();

  const { query } = useRouter();

  const getListData = useCallback(
    (productId: number): string | undefined => {
      if (query.list && (query.list as string).includes('account_promo')) {
        return 'Account Promo';
      }

      if (query.list && (query.list as string).includes('search')) {
        return 'search';
      }

      return getProductList(productId, '');
    },
    [getProductList, query.list]
  );

  const onAddToCart = useCallback(async () => {
    if (!selectedVariantId) {
      return null;
    }

    const promises: Array<Promise<any>> = [];

    if (orderType === OrderType.SUBSCRIPTION && subscriptionInterval) {
      promises.push(
        addSubItemToCart({
          variantId: selectedVariantId + '',
          quantity: variantQuantity,
          interval: subscriptionInterval,
        })
      );
      if (crossSellProductDetails && !!crossSellProductQuantity) {
        promises.push(
          addProductsToCart([
            {
              variantId: crossSellProductDetails.variantId + '',
              quantity: crossSellProductQuantity,
            },
          ]).then(() => {
            setCrossSellProductQuantity(0);
          })
        );
      }
    } else {
      promises.push(
        addProductsToCart([
          {
            variantId: selectedVariantId + '',
            quantity: variantQuantity,
          },
          ...(crossSellProductDetails
            ? [
                {
                  variantId: crossSellProductDetails.variantId + '',
                  quantity: crossSellProductQuantity,
                },
              ]
            : []),
        ]).then(() => {
          setCrossSellProductQuantity(0);
        })
      );
    }

    await Promise.all(promises);

    gtm({
      group: Gtm.GroupName.Product,
      name: Gtm.ProductEventName.AddToCart,
      data: {
        list: getListData(product.id),
        item: {
          productId: product.id + '',
          variantTitle: selectedVariant!.title,
          variantId: selectedVariantId + '',
          productTitle: product.title,
          productType: product.productType,
          tags: product.tags.join(','),
          sku: selectedVariant!.sku,
          quantity: variantQuantity,
          interval:
            orderType === OrderType.SUBSCRIPTION
              ? subscriptionInterval
              : undefined,
          price: Math.round(price * 100),
        },
      },
    });
    crossSellProductDetails &&
      crossSellProductQuantity > 0 &&
      gtm({
        group: Gtm.GroupName.Product,
        name: Gtm.ProductEventName.AddToCart,
        data: {
          list: getProductList(
            crossSellProductDetails.productId,
            'PDP Cross Sell'
          ),
          item: {
            productId: product.id + '',
            variantTitle: selectedVariant!.title,
            variantId: selectedVariantId + '',
            productTitle: product.title,
            productType: product.productType,
            tags: product.tags.join(','),
            sku: selectedVariant!.sku,
            quantity: variantQuantity,
            interval:
              orderType === OrderType.SUBSCRIPTION
                ? subscriptionInterval
                : undefined,
            price: Math.round(price * 100),
          },
        },
      });
    crossSellProductDetails &&
      crossSellProductQuantity > 0 &&
      gtm({
        group: Gtm.GroupName.Product,
        name: Gtm.ProductEventName.AddToCart,
        data: {
          list: getProductList(
            crossSellProductDetails.productId,
            'PDP Cross Sell'
          ),
          item: {
            productId: crossSellProductDetails.productId + '',
            variantTitle: crossSellProductDetails.variantTitle,
            variantId: crossSellProductDetails.variantId + '',
            productTitle: crossSellProductDetails.productTitle,
            productType: crossSellProductDetails.productType,
            tags: crossSellProductDetails.tags,
            sku: crossSellProductDetails.sku,
            quantity: crossSellProductQuantity,
            price: crossSellProductDetails.price,
          },
        },
      });
  }, [
    addProductsToCart,
    addSubItemToCart,
    crossSellProductDetails,
    crossSellProductQuantity,
    getListData,
    getProductList,
    orderType,
    price,
    product.id,
    product.productType,
    product.tags,
    product.title,
    selectedVariant,
    selectedVariantId,
    setCrossSellProductQuantity,
    subscriptionInterval,
    variantQuantity,
  ]);

  return {
    onAddToCart: onAddToCart,
  };
};

export default useProductDetailsCart;
